import { deriveEyebrow, formatINR, placeholderGradient, firstLetter } from "../../lib/product";
import { getProductAvailability } from "../../lib/preorder";
import StatusBadge from "./StatusBadge";
import DispatchLine from "./DispatchLine";
import { HOME } from "../../constants/testIds";

// Loadout product card — universal card used everywhere on the site
// (bestsellers grid, PLP, related, etc.).  White surface, hover swaps
// border to accent, lifts 2px + soft shadow.
export default function ProductCard({ product, className = "" }) {
  const all = (product.variants?.edges || []).map((e) => e.node);
  const variant = all.find((v) => (v?.quantityAvailable ?? 0) > 0) || product.firstVariant || null;
  const availability = getProductAvailability(variant, product.tags || [], new Date());
  const isPre = availability.status === "PRE_ORDER";

  const priceStr = formatINR(product.priceRange?.minVariantPrice);
  const compareStr = formatINR(product.compareAtPriceRange?.minVariantPrice);
  const hasCompare =
    compareStr &&
    Number(product.compareAtPriceRange?.minVariantPrice?.amount || 0) >
      Number(product.priceRange?.minVariantPrice?.amount || 0);

  const eyebrow = deriveEyebrow(product);
  const image = product.featuredImage || product.images?.[0];
  const href = `/p/${product.handle}`;

  return (
    <a
      data-testid={HOME.productCard}
      href={href}
      className={
        "group block overflow-hidden bg-white border border-transparent transition-all duration-300 " +
        "hover:border-[#FF9F1C] hover:-translate-y-0.5 " +
        "hover:shadow-[0_8px_24px_rgba(0,0,0,0.06)] " +
        className
      }
    >
      <div className="relative w-full overflow-hidden bg-[#FAFAFA] aspect-[4/5]">
        {image?.url ? (
          <img
            src={image.url}
            alt={image.altText || product.title}
            loading="lazy"
            className="h-full w-full object-contain p-3 md:p-4 transition-transform duration-[320ms] ease-out group-hover:scale-[1.04]"
          />
        ) : (
          <div
            className="flex h-full w-full items-center justify-center"
            style={{ background: placeholderGradient(product.handle) }}
          >
            <span className="font-display text-white/70 text-6xl">
              {firstLetter(product.title)}
            </span>
          </div>
        )}
        {!isPre && (
          <div className="absolute left-3 top-3">
            <StatusBadge status={availability.status} />
          </div>
        )}
      </div>

      <div className="p-4 space-y-2">
        <div
          className="font-mono text-[11px] uppercase tracking-[0.04em] text-[#8A8A8A] min-h-[16px] truncate"
          title={eyebrow || ""}
        >
          {eyebrow || (product.productType || "PRODUCT").toUpperCase()}
        </div>
        <h3
          data-testid={HOME.productTitle}
          className="text-[15px] font-sans font-semibold text-[#0A0A0A] leading-snug line-clamp-2 min-h-[42px]"
          title={product.title}
        >
          {product.title}
        </h3>
        <div className="flex items-baseline gap-2 flex-wrap">
          {hasCompare && (
            <span className="font-mono text-[12px] text-[#8A8A8A] line-through">
              {compareStr}
            </span>
          )}
          <span
            data-testid={HOME.productPrice}
            className="font-mono text-[14px] font-bold text-[#0A0A0A]"
          >
            {priceStr || "—"}
          </span>
          {!isPre && (
            <span
              className="font-mono text-[11px] uppercase tracking-[0.04em] font-semibold"
              style={{ color: "var(--dispatch-instock)" }}
            >
              · IN STOCK
            </span>
          )}
        </div>
        <DispatchLine variant={variant} tags={product.tags || []} />
      </div>
    </a>
  );
}
